/**
 * Multi-chain network context (docs/multichain-plan.md, Phase 4).
 *
 * Fetches the runtime chain registry from GET /api/v1/chains (killing the
 * build-time VITE_* chain coupling), remembers the user's chosen network in
 * localStorage, and exposes helpers for chain-qualified DIDs.
 */
import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react';

const API_BASE = import.meta.env.VITE_API_URL || '/api/v1';
const STORAGE_KEY = 'ida_network';

export interface ChainInfo {
  chainId: number;
  name: string;
  shortName: string;
  rpcUrls: string[];
  explorer: string;
  nativeCurrency: { name: string; symbol: string; decimals: number };
  testnet: boolean;
  enabled: boolean;
  recommended: boolean;
  faucet: string | null;
  didFormat: 'bare' | 'qualified';
  contracts: Record<string, string>;
}

interface NetworkContextValue {
  /** All chains the deployment knows about (enabled and pending). */
  chains: ChainInfo[];
  homeChainId: number;
  /** The user's currently selected network (always an enabled chain). */
  selected: ChainInfo | null;
  setNetwork: (chainId: number) => void;
  loading: boolean;
  /** Parse the chain out of a did:adi string (home chain for bare wallet DIDs). */
  chainForDID: (did: string) => ChainInfo | null;
}

const NetworkContext = createContext<NetworkContextValue | null>(null);

export function useNetwork(): NetworkContextValue {
  const ctx = useContext(NetworkContext);
  if (!ctx) throw new Error('useNetwork must be used within NetworkProvider');
  return ctx;
}

// Fallback single-chain registry from build-time env (used only if /chains fails).
function envFallback(): { homeChainId: number; chains: ChainInfo[] } {
  const chainId = Number(import.meta.env.VITE_BLOCKCHAIN_CHAIN_ID ?? 36900);
  return {
    homeChainId: chainId,
    chains: [{
      chainId,
      name: `Chain ${chainId}`,
      shortName: `chain-${chainId}`,
      rpcUrls: [import.meta.env.VITE_BLOCKCHAIN_RPC_URL ?? ''],
      explorer: import.meta.env.VITE_BLOCK_EXPLORER_URL ?? '',
      nativeCurrency: { name: 'ADI', symbol: 'ADI', decimals: 18 },
      testnet: false,
      enabled: true,
      recommended: true,
      faucet: null,
      didFormat: 'bare',
      contracts: {},
    }],
  };
}

const ETH_ADDR = /^0x[0-9a-fA-F]{40}$/;
const CHAIN_SEG = /^[1-9][0-9]{0,9}$/;

export function NetworkProvider({ children }: { children: ReactNode }) {
  const [chains, setChains] = useState<ChainInfo[]>([]);
  const [homeChainId, setHomeChainId] = useState<number>(36900);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      let reg: { homeChainId: number; chains: ChainInfo[] };
      try {
        const res = await fetch(`${API_BASE}/chains`);
        if (!res.ok) throw new Error(`chains: ${res.status}`);
        const data = await res.json();
        const body = data.data ?? data;
        reg = {
          homeChainId: Number(body.homeChainId ?? 36900),
          chains: Object.values(body.chains ?? {}) as ChainInfo[],
        };
        if (!reg.chains.length) reg = envFallback();
      } catch {
        reg = envFallback();
      }
      if (cancelled) return;
      setChains(reg.chains);
      setHomeChainId(reg.homeChainId);

      // Restore the saved choice if it's still an enabled chain; else home chain.
      const saved = Number(localStorage.getItem(STORAGE_KEY));
      const valid = reg.chains.find((c) => c.chainId === saved && c.enabled);
      const home = reg.chains.find((c) => c.chainId === reg.homeChainId && c.enabled);
      setSelectedId(valid?.chainId ?? home?.chainId ?? reg.chains.find((c) => c.enabled)?.chainId ?? null);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  const setNetwork = useCallback((chainId: number) => {
    setSelectedId(chainId);
    localStorage.setItem(STORAGE_KEY, String(chainId));
  }, []);

  const chainForDID = useCallback((did: string): ChainInfo | null => {
    if (!did?.startsWith('did:adi:')) return null;
    const segs = did.slice('did:adi:'.length).split(':');
    if (segs.length === 2 && CHAIN_SEG.test(segs[0]) && ETH_ADDR.test(segs[1])) {
      return chains.find((c) => c.chainId === Number(segs[0])) ?? null;
    }
    if (segs.length === 1 && ETH_ADDR.test(segs[0])) {
      return chains.find((c) => c.chainId === homeChainId) ?? null;
    }
    return null; // key-based — chainless
  }, [chains, homeChainId]);

  const selected = chains.find((c) => c.chainId === selectedId) ?? null;

  return (
    <NetworkContext.Provider value={{ chains, homeChainId, selected, setNetwork, loading, chainForDID }}>
      {children}
    </NetworkContext.Provider>
  );
}
